import { useTranslation } from 'react-i18next';
import {
    extractGeneratedAttachmentIds,
    getToolIcon,
    getToolStatusKey,
    getToolUnavailableKey,
    isToolOutputError,
} from '../components/toolDisplay';
import { stripAllMarkers } from '../components/messageContent';
import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GET_CHAT_USAGE, GET_CONVERSATION, GET_CONVERSATIONS } from '../queries';
import { cancelChatStream, streamConversationMessage } from '../api/streamConversation';

const REVEAL_STEP_MS = 60;
const revealTokens = (content: string): string[] => content.split(/(\s+)/);

const RECOVERY_POLL_MS = 3000;
const RECOVERY_DEADLINE_MS = 120000;

const answerArrived = (messages: { role: string }[]): boolean => {
    const lastUserIndex = messages.map((m) => m.role).lastIndexOf('user');
    return lastUserIndex >= 0 && messages.slice(lastUserIndex + 1).some((m) => m.role === 'assistant');
};

const abortableDelay = (ms: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve) => {
        const finish = () => {
            signal.removeEventListener('abort', finish);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(finish, ms);
        signal.addEventListener('abort', finish);
    });

const SLOW_AFTER_MS = 30000;

export type ChatErrorKind = 'contentFilter' | 'unavailable' | 'chatDisabled' | 'connectionLost' | 'sendFailed';

const errorKind = (code?: string): ChatErrorKind => {
    switch (code) {
        case 'content_filter':
            return 'contentFilter';
        case 'unavailable':
            return 'unavailable';
        case 'CHAT_DISABLED':
            return 'chatDisabled';
        case 'CONNECTION_LOST':
            return 'connectionLost';
        default:
            return 'sendFailed';
    }
};

const refetchStaleQueries = (client: ApolloClient<object>, assistantMessage: any) => {
    const include: any[] = [GET_CONVERSATIONS, GET_CHAT_USAGE];
    if (extractGeneratedAttachmentIds(assistantMessage.toolCalls).size > 0) include.push(GET_CONVERSATION);
    client.refetchQueries({ include });
};

const toClientMessage = (raw: any) => ({
    __typename: 'AgentConversationMessageType',
    id: raw.id,
    role: raw.role,
    content: raw.content,
    status: raw.status,
    toolCalls: raw.tool_calls,
    createdAt: raw.created_at,
    scheduleName: null,
    scheduleRunStatus: '',
    scheduleRunError: '',
    scheduleTestRun: false,
    schedule: null,
    attachments: [],
});

export interface ToolStatus {
    text: string;
    Icon: ReturnType<typeof getToolIcon>;
    // a tool call (or file search) is running, as opposed to the model thinking between steps
    isWorking: boolean;
}

export const useConversationStream = (conversationId: string) => {
    const { t } = useTranslation('chat');
    const client = useApolloClient();
    const [isStreaming, setIsStreaming] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const [toolStatus, setToolStatus] = useState<ToolStatus | null>(null);
    const [streamingMessage, setStreamingMessage] = useState<any>(null);
    const [chatError, setChatError] = useState<ChatErrorKind | null>(null);
    const [unsentMessage, setUnsentMessage] = useState<string | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [budgetExceeded, setBudgetExceeded] = useState(false);
    const [isSlow, setIsSlow] = useState(false);
    const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastAttemptRef = useRef<{ message: string; attachmentIds: string[]; editMessageId: string | null } | null>(
        null
    );

    const patchCache = useCallback(
        (patch: (data: any) => any) => {
            client.cache.updateQuery({ query: GET_CONVERSATION, variables: { id: conversationId } }, (data) =>
                data?.agentConversation ? patch(data) : data
            );
        },
        [client, conversationId]
    );

    const appendMessage = useCallback(
        (message: any, conversationPatch?: { title?: string; contextTokens?: number }) => {
            patchCache((data) => {
                const convo = data.agentConversation;
                // a concurrent GET_CONVERSATION (notably on a brand-new chat) may already hold this row;
                // dedupe by id so it isn't rendered twice
                const messages = convo.messages.some((m: any) => m.id === message.id)
                    ? convo.messages
                    : [...convo.messages, message];
                return { agentConversation: { ...convo, ...conversationPatch, messages } };
            });
        },
        [patchCache]
    );

    const appendTurn = useCallback(
        (
            userMessageId: string,
            userContent: string,
            attachmentIds: string[],
            assistantMessage: any,
            conversationPatch?: { title?: string; contextTokens?: number }
        ) => {
            patchCache((data) => {
                const convo = data.agentConversation;
                const userMessage = {
                    __typename: 'AgentConversationMessageType',
                    id: userMessageId,
                    role: 'user',
                    content: userContent,
                    status: 'COMPLETED',
                    toolCalls: null,
                    createdAt: new Date().toISOString(),
                    scheduleName: null,
                    scheduleRunStatus: '',
                    scheduleRunError: '',
                    scheduleTestRun: false,
                    schedule: null,
                    attachments: (convo.attachments || []).filter((attachment: any) =>
                        attachmentIds.includes(attachment.id)
                    ),
                };
                // a concurrent GET_CONVERSATION (notably on a brand-new chat) may already hold the
                // just-persisted user message; only add rows not already in the thread (dedupe by id)
                const existingIds = new Set(convo.messages.map((m: any) => m.id));
                const additions = [userMessage, assistantMessage].filter((m) => !existingIds.has(m.id));
                return {
                    agentConversation: {
                        ...convo,
                        ...conversationPatch,
                        messages: [...convo.messages, ...additions],
                    },
                };
            });
        },
        [patchCache]
    );

    const truncateAt = useCallback(
        (messageId: string) => {
            patchCache((data) => {
                const idx = data.agentConversation.messages.findIndex((m: any) => m.id === messageId);
                if (idx < 0) return data;
                return {
                    agentConversation: {
                        ...data.agentConversation,
                        messages: data.agentConversation.messages.slice(0, idx),
                    },
                };
            });
        },
        [patchCache]
    );

    const recoveryAbortRef = useRef<AbortController | null>(null);

    useEffect(
        () => () => {
            recoveryAbortRef.current?.abort();
            if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
        },
        []
    );

    const resetSlowTimer = useCallback((running: boolean) => {
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
        setIsSlow(false);
        slowTimerRef.current = running ? setTimeout(() => setIsSlow(true), SLOW_AFTER_MS) : null;
    }, []);

    const recoverLostConnection = useCallback(async (): Promise<boolean> => {
        const recovery = new AbortController();
        recoveryAbortRef.current = recovery;
        setToolStatus({ text: t('thread.recovering'), Icon: getToolIcon(''), isWorking: false });
        try {
            const deadline = Date.now() + RECOVERY_DEADLINE_MS;
            while (Date.now() < deadline) {
                await abortableDelay(RECOVERY_POLL_MS, recovery.signal);
                if (recovery.signal.aborted) return false;
                const { data } = await client
                    .query({
                        query: GET_CONVERSATION,
                        variables: { id: conversationId },
                        fetchPolicy: 'network-only',
                    })
                    .catch(() => ({ data: null }));
                if (answerArrived(data?.agentConversation?.messages ?? [])) {
                    client.refetchQueries({ include: [GET_CONVERSATIONS, GET_CHAT_USAGE] });
                    return true;
                }
            }
            return false;
        } finally {
            recoveryAbortRef.current = null;
        }
    }, [client, conversationId, t]);

    const runStream = useCallback(
        async (message: string, attachmentIds: string[], editMessageId: string | null) => {
            lastAttemptRef.current = { message, attachmentIds, editMessageId };
            setIsStreaming(true);
            setChatError(null);
            setUnsentMessage(null);
            setTruncated(false);
            setStreamingMessage(null);
            setToolStatus(null);
            resetSlowTimer(true);
            let userMessageId: string | null = null;
            const onEvent =
                <T extends unknown[]>(handler: (...args: T) => void) =>
                (...args: T) => {
                    resetSlowTimer(true);
                    handler(...args);
                };

            try {
                await streamConversationMessage(conversationId, message, attachmentIds, editMessageId, {
                    onTurnStarted: onEvent(({ user_message_id }) => {
                        userMessageId = user_message_id;
                        if (lastAttemptRef.current) {
                            lastAttemptRef.current = {
                                ...lastAttemptRef.current,
                                editMessageId: user_message_id,
                            };
                        }
                    }),
                    onFileSearch: onEvent(() =>
                        setToolStatus({
                            text: t('thread.tools.statusFileSearch'),
                            Icon: getToolIcon(''),
                            isWorking: true,
                        })
                    ),
                    onToolCallStarted: onEvent(({ tool_name }) =>
                        setToolStatus({
                            text: t(getToolStatusKey(tool_name)),
                            Icon: getToolIcon(tool_name),
                            isWorking: true,
                        })
                    ),
                    onToolCallFinished: onEvent(({ tool_name, tool_output }) => {
                        if (isToolOutputError(tool_output)) {
                            setToolStatus({
                                text: t(getToolUnavailableKey(tool_name)),
                                Icon: getToolIcon(tool_name),
                                isWorking: true,
                            });
                        }
                    }),
                    onThinking: onEvent(() =>
                        setToolStatus({ text: t('thread.thinking'), Icon: getToolIcon(''), isWorking: false })
                    ),
                    onDone: ({ message: rawMessage, conversation, truncated: wasTruncated }) => {
                        resetSlowTimer(false);
                        setToolStatus(null);
                        setTruncated(Boolean(wasTruncated));
                        const clientMessage = toClientMessage(rawMessage);
                        const conversationPatch = conversation && {
                            title: conversation.title,
                            contextTokens: conversation.context_tokens,
                        };
                        if (userMessageId) {
                            appendTurn(userMessageId, message, attachmentIds, clientMessage, conversationPatch);
                        } else {
                            appendMessage(clientMessage, conversationPatch);
                        }

                        refetchStaleQueries(client, clientMessage);

                        const tokens = revealTokens(stripAllMarkers(clientMessage.content));
                        const stepMs = Math.min(REVEAL_STEP_MS, 5000 / tokens.length);
                        let shown = 0;
                        const step = () => {
                            shown = shown + 1;
                            setStreamingMessage({ ...clientMessage, content: tokens.slice(0, shown).join('') });
                            if (shown < tokens.length) setTimeout(step, stepMs);
                            else setStreamingMessage(null);
                        };
                        step();
                    },
                    onCancelled: ({ message: rawMessage, conversation }) => {
                        resetSlowTimer(false);
                        setToolStatus(null);
                        // a turn cancelled before producing any content/tool calls has no message row at all
                        if (!rawMessage) return;
                        const clientMessage = toClientMessage(rawMessage);
                        const conversationPatch = conversation && {
                            title: conversation.title,
                            contextTokens: conversation.context_tokens,
                        };
                        if (userMessageId) {
                            appendTurn(userMessageId, message, attachmentIds, clientMessage, conversationPatch);
                        } else {
                            appendMessage(clientMessage, conversationPatch);
                        }
                        refetchStaleQueries(client, clientMessage);
                    },
                });
            } catch (err) {
                console.error('Chat stream failed:', err);
                resetSlowTimer(false);
                setStreamingMessage(null);
                const code = (err as { code?: string })?.code;
                const recovered = code === 'CONNECTION_LOST' && (await recoverLostConnection());
                // otherwise the failed message vanishes from the thread until the page is reloaded
                if (!recovered) {
                    if (userMessageId) client.refetchQueries({ include: [GET_CONVERSATION] });
                    else setUnsentMessage(message);
                }
                if (code === 'TOOL_CALL_BUDGET_EXCEEDED') setBudgetExceeded(true);
                // the refetched usage reads 100%, which useChatUsage turns into the banner + disabled composer;
                // no retry offer since the next attempt would be blocked the same way
                else if (code === 'CHAT_LLM_COST_LIMIT_EXCEEDED') client.refetchQueries({ include: [GET_CHAT_USAGE] });
                else if (!recovered) setChatError(errorKind(code));
                setToolStatus(null);
            } finally {
                setIsStreaming(false);
                setIsStopping(false);
            }
        },
        [conversationId, appendMessage, appendTurn, recoverLostConnection, resetSlowTimer, t]
    );

    const stopStreaming = useCallback(async () => {
        if (recoveryAbortRef.current) {
            recoveryAbortRef.current.abort();
            await cancelChatStream(conversationId).catch(() => undefined);
            return;
        }
        setIsStopping(true);
        try {
            await cancelChatStream(conversationId);
        } catch (err) {
            console.error('Failed to cancel chat stream:', err);
            setIsStopping(false);
        }
    }, [conversationId]);

    return {
        isStreaming,
        isStopping,
        toolStatus,
        streamingMessage,
        chatError,
        unsentMessage,
        failedMessage: lastAttemptRef.current?.message ?? null,
        clearFailure: () => {
            setChatError(null);
            setUnsentMessage(null);
        },
        isSlow,
        truncated,
        budgetExceeded,
        setBudgetExceeded,
        stopStreaming,
        sendMessage: (message: string, attachmentIds: string[] = []) => runStream(message, attachmentIds, null),
        editAndResend: (messageId: string, message: string) => {
            truncateAt(messageId);
            return runStream(message, [], messageId);
        },
        retryLastMessage: async () => {
            const attempt = lastAttemptRef.current;
            if (!attempt) return;
            // the failed turn may still be running server-side (e.g. after a dropped stream) and would
            // race the retry on the same conversation; cancellation is best-effort
            await cancelChatStream(conversationId).catch(() => undefined);
            return runStream(attempt.message, attempt.attachmentIds, attempt.editMessageId);
        },
    };
};
